import { useState } from "react";
import { Copy, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

interface CredentialsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  username: string;
  temporaryPassword: string;
  email: string;
  onDone: () => void;
}

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

export function CredentialsDialog({
  open,
  onOpenChange,
  username,
  temporaryPassword,
  email,
  onDone,
}: CredentialsDialogProps) {
  const [copiedField, setCopiedField] = useState<"username" | "password" | null>(
    null,
  );

  const handleCopy = async (field: "username" | "password", value: string) => {
    const ok = await copyText(value);
    if (ok) {
      setCopiedField(field);
      window.setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    onDone();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>بيانات الدخول للمستخدم الجديد</DialogTitle>
          <DialogDescription>
            شارك هذه البيانات مع المستخدم. لن تتمكن من عرض كلمة المرور مرة أخرى
            بعد إغلاق هذه النافذة. سيُطلب من المستخدم تغيير كلمة المرور عند أول
            تسجيل دخول.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>اسم المستخدم</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-md border bg-muted px-3 py-2 text-sm">
                {username}
              </code>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => handleCopy("username", username)}
                aria-label="نسخ اسم المستخدم"
              >
                {copiedField === "username" ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>كلمة المرور</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-md border bg-muted px-3 py-2 text-sm break-all">
                {temporaryPassword}
              </code>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => handleCopy("password", temporaryPassword)}
                aria-label="نسخ كلمة المرور"
              >
                {copiedField === "password" ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            يمكن تسجيل الدخول باستخدام اسم المستخدم أو البريد الإلكتروني (
            {email}).
          </p>
        </div>

        <DialogFooter>
          <Button type="button" onClick={handleClose} className="w-full sm:w-auto">
            تم — العودة إلى قائمة المستخدمين
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
